import React, { useMemo, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  MenuItem,
  Divider,
} from "@mui/material";
import { useMutation, useQuery } from "@apollo/client";
import {
  NEWSLETTER_BANNER_QUERY,
  CREATE_NEWSLETTER_SUBSCRIBER_MUTATION,
} from "../graphql/newsletter";
import { buildNewsletterBannerImageUrl } from "../api/newsletterBanner";

const genders = [
  { value: "", label: "Select" },
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "other", label: "Other" },
];

export default function NewsletterSubscribePage() {
  const { data } = useQuery(NEWSLETTER_BANNER_QUERY);

  const [createSubscriber, { loading }] = useMutation(
    CREATE_NEWSLETTER_SUBSCRIBER_MUTATION,
  );

  const bannerUrl = useMemo(() => {
    const path = data?.newsletterBanner?.image;
    return buildNewsletterBannerImageUrl(path);
  }, [data]);

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    gender: "",
    phone: "",
    address: "",
  });

  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: any) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    setError(null);
    setSuccess(null);

    if (!form.fullName.trim() || !form.email.trim()) {
      setError("Full name and email are required.");
      return;
    }

    try {
      const res = await createSubscriber({
        variables: {
          fullName: form.fullName.trim(),
          email: form.email.trim(),
          gender: form.gender || null,
          phone: form.phone.trim() || null,
          address: form.address.trim() || null,
        },
      });

      const errors = res.data?.createNewsletterSubscriber?.errors || [];
      if (errors.length) {
        setError(errors.join(", "));
        return;
      }

      setSuccess(
        "Subscribed! You’ll receive updates, offers and discounts soon.",
      );
      setForm({ fullName: "", email: "", gender: "", phone: "", address: "" });
    } catch (e: any) {
      setError(e.message || "Failed to subscribe.");
    }
  };

  return (
    <Box sx={{ py: { xs: 3, md: 3 }, bgcolor: "#fafafa" }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #eee",
              bgcolor: "#fff",
            }}
          >
            <Box
              sx={{
                position: "relative",
                bgcolor: "#f3f3f3",
                width: "100%",
                aspectRatio: "1600 / 600",
                maxHeight: { xs: 260, sm: 320, md: 420, lg: 460 },
                overflow: "hidden",
              }}
            >
              {bannerUrl ? (
                <Box
                  component="img"
                  loading="lazy"
                  decoding="async"
                  src={bannerUrl}
                  alt="Newsletter banner"
                  sx={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "grid",
                    placeItems: "center",
                    px: 2,
                  }}
                >
                  <Typography sx={{ color: "text.secondary" }}>
                    Banner will appear here once admin uploads it.
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>

          {/* Form */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #eee",
              p: { xs: 2, md: 3 },
              bgcolor: "#fff",
            }}
          >
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}
              {success && <Alert severity="success">{success}</Alert>}

              <Typography
                sx={{
                  fontFamily: `"Bodoni Moda", serif`,
                  fontWeight: 700,
                  fontSize: 22,
                }}
              >
                Join Haroon Sharif Updates
              </Typography>

              <Typography sx={{ color: "text.secondary" }}>
                Share your details and we’ll keep you posted with the latest
                collections.
              </Typography>

              <Divider />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Full Name"
                  value={form.fullName}
                  onChange={set("fullName")}
                  fullWidth
                />
                <TextField
                  label="Email"
                  value={form.email}
                  onChange={set("email")}
                  type="email"
                  fullWidth
                />
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  select
                  label="Gender"
                  value={form.gender}
                  onChange={set("gender")}
                  fullWidth
                >
                  {genders.map((g) => (
                    <MenuItem key={g.value} value={g.value}>
                      {g.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Phone"
                  value={form.phone}
                  onChange={set("phone")}
                  fullWidth
                />
              </Stack>

              <TextField
                label="Address"
                value={form.address}
                onChange={set("address")}
                fullWidth
                multiline
                minRows={2}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={1}>
                <Button
                  variant="contained"
                  onClick={submit}
                  disabled={loading}
                  sx={{
                    borderRadius: 999,
                    py: 1,
                    backgroundColor: "#111",
                    fontSize: 12,
                    minWidth: 140,
                    letterSpacing: 0.4,
                    boxShadow: "0 6px 16px rgba(0,0,0,.15)",
                    "&:hover": {
                      backgroundColor: "#333",
                      boxShadow: "0 8px 18px rgba(0,0,0,.18)",
                    },
                  }}
                >
                  {loading ? "Submitting..." : "Subscribe"}
                </Button>

                <Typography
                  sx={{
                    color: "text.secondary",
                    fontSize: 13,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  By subscribing, you agree to receive updates.
                </Typography>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}
